import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconColorType, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { FontColorType, Text, useTooltip, styles as commonStyles } from '@frontend/design-system';
import { ComparisonPeriodLabel } from '../../constants';
import { ComparisonPeriodType } from '../../types';

type PercentChangeType = 'increase' | 'decrease' | 'neutral';
type DisplayDataType = { iconName: IconName; iconColor: IconColorType; fontColor: FontColorType };
type PercentChangeDisplayMapType = Record<PercentChangeType, Partial<DisplayDataType>>;

const DefaultDisplayMap: Record<PercentChangeType, DisplayDataType> = {
  increase: {
    iconName: 'up-small',
    iconColor: 'success',
    fontColor: 'success',
  },
  decrease: {
    iconName: 'down-small',
    iconColor: 'error',
    fontColor: 'error',
  },
  neutral: {
    iconName: 'up-small',
    iconColor: 'subdued',
    fontColor: 'subdued',
  },
};

interface PercentChangeIndicatorProps {
  comparisonPeriod: ComparisonPeriodType;
  percentChange: number;
  percentChangeDisplayMap?: PercentChangeDisplayMapType;
  size?: 'small' | 'medium';
}

export const PercentChangeIndicator = ({
  percentChange,
  comparisonPeriod,
  percentChangeDisplayMap = DefaultDisplayMap,
  size = 'small',
}: PercentChangeIndicatorProps) => {
  const { t } = useTranslation('callIntelDashboardWidgets');
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement: 'top',
  });

  const { iconColor, fontColor, iconName, value, tooltipText } = useMemo(() => {
    const changeType = percentChange > 0 ? 'increase' : percentChange < 0 ? 'decrease' : 'neutral';

    const iconColor: IconColorType =
      percentChangeDisplayMap[changeType]?.iconColor || DefaultDisplayMap[changeType].iconColor;
    const fontColor: FontColorType =
      percentChangeDisplayMap[changeType]?.fontColor || DefaultDisplayMap[changeType].fontColor;
    const iconName: IconName = percentChangeDisplayMap[changeType]?.iconName || DefaultDisplayMap[changeType].iconName;

    const value = Math.abs(percentChange);

    const timePeriod = ComparisonPeriodLabel[comparisonPeriod];
    const tooltipText =
      changeType === 'decrease'
        ? t('Down {{count}}% since Last {{timePeriod}}.', { count: value, timePeriod })
        : changeType === 'increase'
        ? t('Up {{count}}% since Last {{timePeriod}}.', { count: value, timePeriod })
        : t('No change since Last {{timePeriod}}.', { timePeriod });

    return {
      iconColor,
      fontColor,
      iconName,
      value,
      tooltipText,
    };
  }, [percentChange, comparisonPeriod, percentChangeDisplayMap, t]);

  return (
    <div css={[commonStyles.flexCenter, { gap: theme.spacing(0.25) }]} {...triggerProps}>
      <Icon name={iconName} color={iconColor} size={size === 'medium' ? 16 : 12} />
      <Text as='span' color={fontColor} size={size}>
        {value}%
      </Text>
      <Tooltip {...tooltipProps}>
        <Text size='medium' as='div' color='white'>
          {tooltipText}
        </Text>
        <Text size='medium' as='div' color='white' css={{ fontStyle: 'italic' }}>
          {t('*Partial comparison. Current time period is ongoing.')}
        </Text>
      </Tooltip>
    </div>
  );
};
